import { jsx, Fragment } from "react/jsx-runtime";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormikContext } from "formik";
import EnvironmentService from "@ane/core/lib/services/EnvironmentService";
import { requestCompleted, requestPending, someRequestIsPending } from "@ane/core/lib/models";
import { SetSubscribersEpicReducer, loadSubscribersEpicReducer, mapSubscribersSetPayload, convertSubscribersV2, convertSubscribers, LoadPickSubscribersField } from "@ane/exp-ux/lib/components/Subscribers";
import { ENTITY_TYPES, SUBSCRIPTION_TYPES } from "@ane/exp-data/lib/models/SubscriberModels";
import ClaimCheckDataProvider from "@ane/exp-ux/lib/components/GetUserAccess/ClaimCheckDataProvider";
import { useHeaderContext } from "../context/HeaderProvider.js";
import { useFeatureEditContext } from "../context/FeatureEditProvider.js";
import { useModuleSelector } from "../../../reducers.js";
import { ActionNames } from "@ane/exp-data/lib/models/masterData/MdsDataplaneModels";
import { ofType } from "redux-observable";
import { concat, of } from "rxjs";
import { mergeMap, mapTo, take } from "rxjs/operators";
import { loadSubscribersEpicReducerV2 } from "@ane/exp-ux/lib/components/Subscribers/LoadSubscribersV2.epics";
import { SetSubscribersEpicReducerV2 } from "@ane/exp-ux/lib/components/Subscribers/SetSubscribersV2.epics";
import { c as getExperimentationGroupId, d as getShowVNextProgressionPage } from "../../../../FeatureDataActions-59549dc8.js";
import { NOTIFICATION_CATEGORY } from "@ane/exp-data/lib/constants/NotificationSettingConstants";
const submitFormSubscribersAction = "SUBMIT_FORM_SUBSCRIBERS_RELOAD";
const submitFormSubscribersActionV2 = "SUBMIT_FORM_SUBSCRIBERS_RELOAD_V2";
const submitFormSubscribersEpic = (action$) => action$.pipe(
  // Filter to our custom action type
  ofType(submitFormSubscribersAction),
  mergeMap(
    (action) => (
      // When we receive this, do:
      concat(
        // Emit the set subscribers action
        of(SetSubscribersEpicReducer.actionCreators.load(action.payload.set)),
        // Emit the "loading" action for load subscribers (to show the loading spinner)
        of(loadSubscribersEpicReducer.actionCreators.loading(action.payload.load)),
        // Emit the load subscribers action after the set subscribers action completes
        action$.pipe(
          ofType(SetSubscribersEpicReducer.actionCreators.loaded(void 0, void 0).type),
          mapTo(loadSubscribersEpicReducer.actionCreators.load(action.payload.load)),
          take(1)
        )
      )
    )
  )
);
const submitFormSubscribersEpicV2 = (action$) => action$.pipe(
  // Filter to our custom action type
  ofType(submitFormSubscribersActionV2),
  mergeMap(
    (action) => (
      // When we receive this, do:
      concat(
        // Emit the set subscribers action
        of(SetSubscribersEpicReducerV2.actionCreators.load(action.payload.setV2)),
        // Emit the "loading" action for load subscribers (to show the loading spinner)
        of(loadSubscribersEpicReducerV2.actionCreators.loading(action.payload.loadV2)),
        // Emit the load subscribers action after the set subscribers action completes
        action$.pipe(
          ofType(SetSubscribersEpicReducerV2.actionCreators.loaded(void 0, void 0).type),
          mapTo(loadSubscribersEpicReducerV2.actionCreators.load(action.payload.loadV2)),
          take(1)
        )
      )
    )
  )
);
const setSubscribersForFeatureAction = (workspaceId, featureId, isUpdate, subscribers) => ({
  type: submitFormSubscribersAction,
  payload: {
    set: {
      workspaceId,
      entityType: "Experiment",
      entityExternalId: featureId,
      entityIsNew: !isUpdate,
      subscriberBaseInfoList: (subscribers == null ? void 0 : subscribers.map(
        (s) => !!s.id ? { id: s.id, type: s.type, tenantId: s.tenantId } : { id: s.primaryText, type: "Email" }
      )) || []
    },
    load: {
      workspaceId,
      entityType: ENTITY_TYPES.EXPERIMENT,
      entityExternalId: featureId,
      fetchPhoto: true,
      includeUser: true
    }
  }
});
const setSubscribersForFeatureActionV2 = (workspaceId, notificationSubscriptionId, featureId, isUpdate, subscribers) => ({
  type: submitFormSubscribersAction,
  payload: {
    setV2: {
      workspaceId,
      notificationSubscription: {
        id: notificationSubscriptionId,
        subscriptionType: SUBSCRIPTION_TYPES.EntityBase,
        entityType: ENTITY_TYPES.EXPERIMENT,
        entityId: featureId,
        subscribers: mapSubscribersSetPayload(subscribers),
        eTag: ""
      }
    },
    loadV2: {
      workspaceId,
      entityType: ENTITY_TYPES.EXPERIMENT,
      entityExternalId: featureId,
      fetchPhoto: true,
      notificationCategory: NOTIFICATION_CATEGORY.ExperimentOperations
    }
  }
});
const FeatureNotificationPicker = () => {
  const dispatch = useDispatch();
  const { values, isSubmitting, setFieldValue } = useFormikContext();
  const featureId = useHeaderContext((ctx) => ctx.currentFeatureId);
  const isEditMode = useFeatureEditContext((ctx) => ctx.isEditMode);
  const workspaceId = useModuleSelector((state) => {
    var _a, _b;
    return (_b = (_a = state.currentUserDetails.userAffinity) == null ? void 0 : _a.data) == null ? void 0 : _b.workspaceId;
  });
  const subscribersAsync = useModuleSelector((state) => state.subscribers.items);
  const setSubscribersAsync = useModuleSelector((state) => state.setSubscribers.value);
  const subscribersAsyncV2 = useModuleSelector((state) => state.subscribersV2.items);
  const setSubscribersAsyncV2 = useModuleSelector((state) => state.setSubscribers.value);
  const experimentationGroupId = useSelector((state) => getExperimentationGroupId(state, { rolloutId: featureId }));
  const showVNextProgressionPage = useSelector(
    (state) => getShowVNextProgressionPage(state, { rolloutId: featureId })
  );
  const notificationSubscription = useSelector(
    (state) => {
      var _a;
      return (_a = state.subscribersV2.items.data) == null ? void 0 : _a.value.filter((s) => s.subscriptionType === SUBSCRIPTION_TYPES.EntityBase);
    }
  );
  const notificationSubscriptionId = !!notificationSubscription && notificationSubscription.length > 0 ? notificationSubscription[0].id : `${workspaceId}_${ENTITY_TYPES.EXPERIMENT}_${featureId}`;
  useEffect(() => {
    var _a, _b, _c;
    if (EnvironmentService.enableUnifiedAPIOfSubscribeMode() && requestCompleted(subscribersAsyncV2.status) && requestPending(setSubscribersAsyncV2.status)) {
      let subscribers = !!((_a = subscribersAsyncV2.data) == null ? void 0 : _a.value.length) ? (_b = subscribersAsyncV2.data) == null ? void 0 : _b.value[0].subscribers : [];
      setFieldValue("subscribers", convertSubscribersV2(subscribers));
    } else if (!EnvironmentService.enableUnifiedAPIOfSubscribeMode() && requestCompleted(subscribersAsync.status) && requestPending(setSubscribersAsync.status)) {
      setFieldValue("subscribers", convertSubscribers((_c = subscribersAsync.data) == null ? void 0 : _c.subscribers));
    }
  }, [subscribersAsync.data, subscribersAsync.status, setSubscribersAsync.data, setSubscribersAsync.status]);
  useEffect(() => {
    if (EnvironmentService.enableUnifiedAPIOfSubscribeMode() && !someRequestIsPending([subscribersAsyncV2.status, setSubscribersAsyncV2.status]) && !!featureId && !!workspaceId) {
      dispatch(
        loadSubscribersEpicReducerV2.actionCreators.load(
          setSubscribersForFeatureActionV2(workspaceId, notificationSubscriptionId, featureId, isEditMode, []).payload.loadV2
        )
      );
    } else if (!EnvironmentService.enableUnifiedAPIOfSubscribeMode() && !someRequestIsPending([subscribersAsync.status, setSubscribersAsync.status]) && !!featureId && !!workspaceId) {
      dispatch(
        loadSubscribersEpicReducer.actionCreators.load(
          setSubscribersForFeatureAction(workspaceId, featureId, isEditMode, []).payload.load
        )
      );
    }
  }, []);
  const onChange = (subscribers) => {
    if (EnvironmentService.enableUnifiedAPIOfSubscribeMode() && !isEditMode && !isSubmitting && requestCompleted(subscribersAsyncV2.status)) {
      dispatch(
        SetSubscribersEpicReducerV2.actionCreators.load(
          setSubscribersForFeatureActionV2(workspaceId, notificationSubscriptionId, featureId, !!featureId, subscribers).payload.setV2
        )
      );
    } else if (!EnvironmentService.enableUnifiedAPIOfSubscribeMode() && !isEditMode && !isSubmitting && requestCompleted(subscribersAsync.status)) {
      dispatch(
        SetSubscribersEpicReducer.actionCreators.load(
          setSubscribersForFeatureAction(workspaceId, featureId, !!featureId, subscribers).payload.set
        )
      );
    }
  };
  return showVNextProgressionPage ? /* @__PURE__ */ jsx(
    ClaimCheckDataProvider,
    {
      experimentationGroupId,
      claims: { allClaims: [ActionNames.Write] },
      render: ({ hasClaims }) => {
        return /* @__PURE__ */ jsx(
          LoadPickSubscribersField,
          {
            required: false,
            name: "subscribers",
            workspaceId,
            isReadOnly: !hasClaims,
            isUpdateEntity: !!featureId,
            entityType: ENTITY_TYPES.EXPERIMENT,
            entityExternalId: featureId,
            subscribers: values.subscribers || [],
            fetchPhoto: true,
            pickerResolveDelay: 500,
            onChange,
            skipLoadOnMount: true,
            notificationCategory: NOTIFICATION_CATEGORY.ExperimentOperations,
            experimentationGroupId
          }
        );
      }
    }
  ) : /* @__PURE__ */ jsx(Fragment, {});
};
export {
  FeatureNotificationPicker,
  setSubscribersForFeatureAction,
  setSubscribersForFeatureActionV2,
  submitFormSubscribersEpic,
  submitFormSubscribersEpicV2
};
//# sourceMappingURL=FeatureNotificationPicker.js.map
